import { usePullRequestCockpitQuery } from '@crowdex/host-kit/renderer/pull-request-cockpit-query';
import type { PullRequestSummary } from '@crowdex/workspace-protocol';
import type {
  PullRequestCockpit,
  PullRequestMutation,
} from '@crowdex/workspace-protocol/pull-request-cockpit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';
import {
  fetchCloudTaskPullRequestCockpit,
  mutateCloudTaskPullRequest,
} from '@/lib/cloud-runtime/cloud-runtime';
import { errorMessage } from '@/lib/error-message';
import {
  mergeNativeGitPullRequest,
  mutateNativeGitPullRequest,
  type NativeGitReview,
  readNativePullRequestCockpit,
} from '@/lib/native-desktop';
import { PULL_REQUEST_COCKPIT_REFRESH_INTERVAL_MS } from '@/lib/side-panel/constants';
import {
  parseStatusLines,
  resolveReviewDiff,
  resolveReviewLabel,
} from '@/lib/side-panel/review-summary';
import type { SidePanelView } from '@/lib/side-panel/side-panel-items';
import type { WorkspaceClient } from '@/lib/workspaces/workspace-client';

export function usePullRequestCockpit({
  activeView,
  client,
  cloudRuntime,
  onPullRequestChange,
  pullRequest,
  pullRequestCockpitOverride,
  reviewDiffOverride,
  taskId,
  workspace,
}: {
  activeView: SidePanelView | null;
  client: WorkspaceClient | null;
  cloudRuntime: CloudRuntime | null;
  onPullRequestChange: () => Promise<void>;
  pullRequest: PullRequestSummary | null;
  pullRequestCockpitOverride?: PullRequestCockpit | null;
  reviewDiffOverride: string | null;
  taskId: string;
  workspace: string | null;
}) {
  const [review, setReview] = useState<NativeGitReview | null>(null);
  const [reviewSource, setReviewSource] = useState<'changes' | 'pull-request'>('changes');
  // Another workspace or pull request shows its changes first.
  const sourceKey = JSON.stringify([workspace, pullRequest?.url]);
  const [sourceKeySeen, setSourceKeySeen] = useState(sourceKey);
  if (sourceKeySeen !== sourceKey) {
    setSourceKeySeen(sourceKey);
    setReviewSource('changes');
  }
  const [reviewError, setReviewError] = useState<string | null>(null);
  const hasPullRequest = pullRequest !== null;
  const cockpitVisible = activeView === 'review';

  const refreshReview = useCallback(async (): Promise<void> => {
    if (client === null || workspace === null) return;
    setReviewError(null);
    try {
      setReview(await client.gitReview());
    } catch (error) {
      setReviewError(errorMessage(error));
    }
  }, [client, workspace]);

  const loadCockpit = useCallback(
    async (signal: AbortSignal): Promise<PullRequestCockpit | null> => {
      if (workspace === null) return null;
      return cloudRuntime === null
        ? readNativePullRequestCockpit(workspace)
        : fetchCloudTaskPullRequestCockpit(taskId, signal);
    },
    [cloudRuntime, taskId, workspace],
  );
  const cockpitQuery = usePullRequestCockpitQuery({
    enabled:
      cockpitVisible &&
      hasPullRequest &&
      workspace !== null &&
      pullRequestCockpitOverride === undefined,
    key: pullRequest?.url ?? null,
    load: loadCockpit,
    pollMs: PULL_REQUEST_COCKPIT_REFRESH_INTERVAL_MS,
    version: pullRequest?.updatedAt ?? null,
  });
  const cockpit =
    pullRequestCockpitOverride === undefined ? cockpitQuery.data : pullRequestCockpitOverride;
  const cockpitError = pullRequestCockpitOverride === undefined ? cockpitQuery.error : null;
  const cockpitLoading = pullRequestCockpitOverride === undefined ? cockpitQuery.loading : false;
  const refreshCockpitQuery = cockpitQuery.refresh;
  const refreshCockpit = useCallback(
    () => (pullRequestCockpitOverride === undefined ? refreshCockpitQuery() : Promise.resolve()),
    [pullRequestCockpitOverride, refreshCockpitQuery],
  );

  async function mergeCurrentPullRequest(input: {
    method: 'merge' | 'squash';
    expectedHeadSha: string;
  }): Promise<void> {
    if (workspace === null) throw new Error('A workspace is required to merge this pull request.');
    if (cloudRuntime === null) {
      await mergeNativeGitPullRequest({ workspaceRoot: workspace, ...input });
    } else {
      await mutateCloudTaskPullRequest(taskId, { action: 'merge', ...input });
    }
    await Promise.all([refreshCockpit(), onPullRequestChange()]);
  }

  async function mutateCurrentPullRequest(mutation: PullRequestMutation): Promise<void> {
    if (workspace === null) throw new Error('A workspace is required to update this pull request.');
    if (cloudRuntime === null) {
      await mutateNativeGitPullRequest({ workspaceRoot: workspace, ...mutation });
    } else {
      await mutateCloudTaskPullRequest(taskId, mutation);
    }
    await Promise.all([refreshCockpit(), onPullRequestChange()]);
  }

  async function updateGitFile(
    path: string,
    action: 'stage' | 'unstage' | 'revert',
  ): Promise<void> {
    if (client === null || workspace === null) return;
    setReviewError(null);
    try {
      await client.updateGitFile(path, action);
      await refreshReview();
    } catch (error) {
      setReviewError(errorMessage(error));
    }
  }

  useEffect(() => {
    if (activeView === 'review' && reviewDiffOverride === null) void refreshReview();
  }, [activeView, refreshReview, reviewDiffOverride]);

  const summary = useMemo(
    () => ({
      diffText: resolveReviewDiff(review, reviewSource),
      reviewLabel: resolveReviewLabel(reviewSource, pullRequest),
      statusLines: parseStatusLines(review?.status),
    }),
    [pullRequest, review, reviewSource],
  );

  return {
    cockpit,
    cockpitError,
    cockpitLoading,
    mergeCurrentPullRequest,
    mutateCurrentPullRequest,
    refreshCockpit,
    reviewError,
    reviewSource,
    setReviewSource,
    summary,
    updateGitFile,
  };
}
